import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  makeStyles,
  LinearProgress,
  Box,
  Typography,
  IconButton,
  Popover
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { DateRangePicker } from 'react-date-range';
import { startOfDay, endOfDay } from "date-fns";
import { 
  UPLOAD_DATA_TAG_REPORT, 
  CLEAR_UPLOAD_STATE 
} from './DataTagConstants';
import Input from '../../components/shared/Input';
import { createStaticRange, dateRangePickerStaticList } from '../../components/common/utilities';
import { formattedDate } from '../../components/views/GenerateReport/utilities';
import { UTCTooltip } from '../../components/views/GenerateReport/UTCTooltip';
import '../../components/views/GenerateReport/Report.scss';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: '650px',
      maxWidth: '750px',
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
  },
  dialogContent: {
    overflowY: 'auto',
  },
  closeButton: {
    padding: '4px',
  },
  uploadArea: {
    border: '2px dashed #D0D0D0',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '16px',
    '&:hover': {
      borderColor: '#081981',
      backgroundColor: '#f5f5f5',
    },
    '&.dragover': {
      borderColor: '#081981',
      backgroundColor: '#e8eaf6',
    }
  },
  uploadIcon: {
    fontSize: '48px',
    color: '#081981',
    marginBottom: '16px',
  },
  fileInput: {
    display: 'none',
  },
  fileName: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressContainer: {
    marginTop: '16px',
  },
  textField: {
    marginLeft: '8px',
    marginRight: '8px',
    width: 100,
  },
  textFieldStartEnd: {
    marginLeft: '8px',
    marginRight: '8px',
    width: 220,
  },
  popover: {
    padding: '16px',
  },
  dateContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
  },
  dateField: {
    flex: 1,
  },
}));

function UploadReportModal({ open, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  
  const [reportName, setReportName] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [disabled, setDisabled] = useState(true);
  
  const currentDate = new Date();
  const [reportPeriodStart, setReportPeriodStart] = useState(currentDate);
  const [reportPeriodEnd, setReportPeriodEnd] = useState(endOfDay(currentDate));
  const [expiryDate, setExpiryDate] = useState(endOfDay(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())));
  
  const [periodAnchorEl, setPeriodAnchorEl] = useState(null);
  const [expiryAnchorEl, setExpiryAnchorEl] = useState(null);

  const uploadLoader = useSelector(
    (state) => state.DataTagReportsReducer.uploadLoader
  );
  
  const uploadProgress = useSelector(
    (state) => state.DataTagReportsReducer.uploadProgress
  );

  const uploadSnackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.uploadSnackBarMessage
  );

  const staticRanges = [...createStaticRange(dateRangePickerStaticList())];

  // Listen to custom upload progress events
  useEffect(() => {
    const handleProgress = (event) => {
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: event.detail });
    };
    
    window.addEventListener('uploadProgress', handleProgress);
    
    return () => {
      window.removeEventListener('uploadProgress', handleProgress);
    };
  }, [dispatch]);

  // Validate form fields
  useEffect(() => {
    if (reportName.trim() && file) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [reportName, file]);

  const handleClose = () => {
    setReportName('');
    setFile(null);
    setReportPeriodStart(currentDate);
    setReportPeriodEnd(endOfDay(currentDate));
    setExpiryDate(endOfDay(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())));
    dispatch({ type: CLEAR_UPLOAD_STATE });
    onClose();
  };

  // Close modal on successful upload
  useEffect(() => {
    if (uploadSnackBarMessage?.status === 'success') {
      setTimeout(() => {
        handleClose();
      }, 1500);
    }
  }, [uploadSnackBarMessage]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = () => {
    if (!disabled && file) {
      
      dispatch({
        type: UPLOAD_DATA_TAG_REPORT,
        payload: {
          file,
          reportName,
          reportPeriodStart: startOfDay(reportPeriodStart).toISOString(),
          reportPeriodEnd: endOfDay(reportPeriodEnd).toISOString(),
          expiryDate: endOfDay(expiryDate).toISOString()
        }
      });
    }
  };

  const openPeriodPopover = Boolean(periodAnchorEl);
  const openExpiryPopover = Boolean(expiryAnchorEl);

  const handlePeriodFieldClick = (event) => {
    setPeriodAnchorEl(event.currentTarget);
  };

  const handleExpiryFieldClick = (event) => {
    setExpiryAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPeriodAnchorEl(null);
    setExpiryAnchorEl(null);
  };

  const renderDateInputWithPopover = (
    label,
    startValue,
    endValue,
    handleClick,
    popoverId,
    openPopover,
    anchorEl,
    handleClose,
    onDateChange,
    isSingleDate = false
  ) => {
    return (
      <div className="generate_report_dates date-picker-flex">
        <div className="generate_report_date_header">{label}</div>
        <div className="generate_report_date_selector">
          <TextField
            value={isSingleDate ? formattedDate(new Date(startValue)) : `${formattedDate(new Date(startValue))} - ${formattedDate(new Date(endValue))}`}
            className={isSingleDate ? classes.textField : classes.textFieldStartEnd}
            InputProps={{
              readOnly: true,
            }}
            onClick={handleClick}
            fullWidth
          />
          <ArrowDropDownIcon
            onClick={handleClick}
            className={`arrow_drop_down ${openPopover ? "icon_clicked" : ""}`}
          />
          <Popover
            id={popoverId}
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <div className={classes.popover}>
              <DateRangePicker
                ranges={[
                  {
                    startDate: new Date(startValue),
                    endDate: new Date(endValue),
                    key: "selection",
                  },
                ]}
                onChange={(ranges) => {
                  onDateChange(ranges.selection.startDate, endOfDay(ranges.selection.endDate));
                }}
                inputRanges={[]}
                staticRanges={staticRanges}
                maxDate={isSingleDate ? undefined : startOfDay(new Date())}
              />
            </div>
          </Popover>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={uploadLoader ? undefined : handleClose}
      className={classes.dialog}
      maxWidth="md"
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6" className="upload-modal-title">
          Upload Report
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={uploadLoader}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        {/* Report Name */}
        <div className="generate_report_content">
          <div className="generate_report_heading">
            <span>Report Name</span>
            <span className="shareReportErrorMessage">*</span>
          </div>
          <div className="generate_report_input">
            <Input
              type="text"
              value={reportName}
              classname="number_input"
              placeholder="Enter Report Name"
              eventHandler={(value) => setReportName(value)}
              disabled={uploadLoader}
            />
          </div>
        </div>

        {/* Report Period */}
        <div className="generate_report_date_container upload-section-spacing">
          <div className="generate_report_heading header-icon">
            Report Period {UTCTooltip()}
          </div>
          <div className={classes.dateContainer}>
            {renderDateInputWithPopover(
              "Period Range",
              reportPeriodStart,
              reportPeriodEnd,
              handlePeriodFieldClick,
              openPeriodPopover ? "period-popover" : undefined,
              openPeriodPopover,
              periodAnchorEl,
              handlePopoverClose,
              (start, end) => {
                setReportPeriodStart(start);
                setReportPeriodEnd(end);
              }
            )}
          </div>
        </div>

        {/* Expiry Date */}
        <div className="generate_report_date_container upload-section-spacing">
          <div className="generate_report_heading header-icon">
            Expiry Date {UTCTooltip()}
          </div>
          <div className={classes.dateContainer}>
            {renderDateInputWithPopover(
              "Expiry Date",
              expiryDate,
              expiryDate,
              handleExpiryFieldClick,
              openExpiryPopover ? "expiry-popover" : undefined,
              openExpiryPopover,
              expiryAnchorEl,
              handlePopoverClose,
              (start) => {
                setExpiryDate(start);
              },
              true
            )}
          </div>
        </div>

        {/* File Upload Area */}
        <div className="upload-section-spacing">
          <div className="generate_report_heading">
            <span>Upload File</span>
            <span className="shareReportErrorMessage">*</span>
          </div>
          <div
            className={`${classes.uploadArea} ${dragOver ? 'dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadClick}
          >
            <CloudUploadIcon className={classes.uploadIcon} />
            <Typography variant="body1" className="upload-drag-text-primary">
              Drag & Drop your file here
            </Typography>
            <Typography variant="body2" className="upload-drag-text-secondary">
              or click to browse
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              className={classes.fileInput}
              onChange={handleFileSelect}
              disabled={uploadLoader}
            />
          </div>
        </div>

        {/* Selected File Display */}
        {file && (
          <div className={classes.fileName}>
            <Typography variant="body2" className="upload-file-name-text">
              {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
            </Typography>
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              disabled={uploadLoader}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}

        {/* Progress Bar */}
        {uploadLoader && (
          <div className={classes.progressContainer}>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="textSecondary">
                  {`${uploadProgress}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" className="upload-progress-text">
              Uploading file...
            </Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions className="upload-dialog-actions">
        <Button
          onClick={handleClose}
          disabled={uploadLoader}
          className="upload-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={disabled || uploadLoader}
          variant="contained"
          className={`upload-submit-button ${disabled || uploadLoader ? 'disabled' : ''}`}
        >
          Upload Report
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UploadReportModal;
